import { useCallback, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

const SORT_PATTERN = /^-?[a-z_]+(,-?[a-z_]+)*$/;

/**
 * `validateSearch` factory for server-side lists. Keeps the URL the single source of truth
 * for page, page size, sort and the listed filter keys (all filters are strings).
 * @param {string[]} filterKeys
 */
export function listSearch(filterKeys = []) {
  /** @param {Record<string, unknown>} search */
  return (search) => {
    /** @type {Record<string, any>} */
    const out = {};
    const page = Number(search.page);
    if (Number.isInteger(page) && page > 1) out.page = page;
    const perPage = Number(search.per_page);
    if (PAGE_SIZES.includes(perPage) && perPage !== DEFAULT_PAGE_SIZE) out.per_page = perPage;
    if (typeof search.sort === 'string' && SORT_PATTERN.test(search.sort)) out.sort = search.sort;
    for (const key of filterKeys) {
      const value = search[key];
      if (typeof value === 'string' && value.trim() !== '') out[key] = value;
      else if (typeof value === 'number' || typeof value === 'boolean') out[key] = String(value);
    }
    return out;
  };
}

/** "-name,city" → [{ id: 'name', desc: true }, { id: 'city', desc: false }] @param {string|undefined} sort */
export function parseSort(sort) {
  if (!sort) return [];
  return sort.split(',').map((part) => (part.startsWith('-') ? { id: part.slice(1), desc: true } : { id: part, desc: false }));
}

/** @param {Record<string, any>} search */
function clean(search) {
  return Object.fromEntries(Object.entries(search).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

/**
 * Binds DataTable state to the URL search params of the current route.
 * @param {Record<string, any>} search  validated search of the route
 */
export function useListState(search) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const update = useCallback(
    /** @param {Record<string, any>} patch */
    (patch) => navigate({ to: pathname, search: (prev) => clean({ ...prev, ...patch }), replace: true }),
    [navigate, pathname],
  );

  const pageSize = search.per_page ?? DEFAULT_PAGE_SIZE;
  const pagination = useMemo(() => ({ pageIndex: (search.page ?? 1) - 1, pageSize }), [search.page, pageSize]);
  const sorting = useMemo(() => parseSort(search.sort), [search.sort]);

  return {
    pagination,
    sorting,
    /** @param {{ pageIndex: number, pageSize: number }} next */
    onPaginationChange: (next) =>
      update({ page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined, per_page: next.pageSize !== DEFAULT_PAGE_SIZE ? next.pageSize : undefined }),
    /** @param {Array<{ id: string, desc: boolean }>} next */
    onSortingChange: (next) => update({ sort: next.map((s) => (s.desc ? `-${s.id}` : s.id)).join(',') || undefined, page: undefined }),
    /** Sets one filter and jumps back to page 1. @param {string} key @param {string|undefined} value */
    setFilter: (key, value) => update({ [key]: value || undefined, page: undefined }),
    /** Base API params (page, per_page, sort) – add `filter` per list. */
    apiParams: { page: search.page ?? 1, per_page: pageSize, sort: search.sort },
  };
}
